import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import NotificationBadge from './NotificationBadge';
import LanguageSwitcher from './common/LanguageSwitcher';
import { globalSearch, formatSearchResult } from '../services/searchService';
import { getApiBaseUrl, getAuthToken } from '../services/api';
import { getImageUrl } from '../utils/imageUrl';
import './TopBar.css';

const API_BASE_URL = getApiBaseUrl();

const TopBar = ({ onSearch, onNotificationClick, onTabChange, onMenuToggle, sidebarOpen = false, isPremium, subscriptionEndsAt, onUpgradeClick }) => {
  const { t } = useTranslation(['navigation', 'common', 'user']);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);
  const notificationsRef = useRef(null);

  // Global search query
  const { data: searchData, isLoading: searching } = useQuery({
    queryKey: ['globalSearch', searchQuery],
    queryFn: () => globalSearch(searchQuery),
    enabled: showSearch && searchQuery.trim().length >= 2,
    staleTime: 30000, // 30 seconds
  });

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // UX Best Practice: Display full name (first_name + last_name)
  // Properly capitalize: First letter uppercase, rest lowercase
  const capitalizeName = (name) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };
  
  const getUserDisplayName = () => {
    if (user.first_name && user.last_name) {
      // Display full name: "Soufiane Rihani"
      return `${capitalizeName(user.first_name)} ${capitalizeName(user.last_name)}`.trim();
    }
    if (user.first_name) {
      // Only first name available
      return capitalizeName(user.first_name);
    }
    if (user.name) {
      // Use full name from name field
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${capitalizeName(nameParts[0])} ${capitalizeName(nameParts.slice(1).join(' '))}`.trim();
      }
      return capitalizeName(nameParts[0]);
    }
    if (user.email) {
      return capitalizeName(user.email.split('@')[0]);
    }
    return 'User';
  };
  
  const userName = getUserDisplayName();
  const roleKey = (user.role || 'user').toLowerCase();
  const userRoleLabel = t(`user:user.profile.${roleKey}`, {
    defaultValue: user.role
      ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
      : 'User',
  });
  const userEmail = user.email || '';
  const userId = user.id;

  // Fetch user-specific notifications
  const fetchNotifications = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/notifications?limit=10&read=false`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  };

  // Fetch notifications using React Query
  const { data: notifications = [], isLoading: notificationsLoading, refetch: refetchNotifications } = useQuery({
    queryKey: ['notifications', userId, roleKey],
    queryFn: fetchNotifications,
    enabled: !!userId,
    refetchInterval: 60000, // Refetch every 60 seconds (reduced polling to avoid 429)
    staleTime: 30000, // 30 seconds
  });

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.success ? data.count : 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  };

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unreadCount', userId, roleKey],
    queryFn: fetchUnreadCount,
    enabled: !!userId,
    refetchInterval: 60000, // Refetch every 60 seconds (reduced polling to avoid 429)
    staleTime: 30000, // 30 seconds
  });

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Get notification icon based on type/category
  const getNotificationIcon = (type, category) => {
    if (category?.includes('student') || category?.includes('course')) {
      return 'school';
    }
    if (category?.includes('teacher') || category?.includes('class')) {
      return 'class';
    }
    if (type === 'warning' || type === 'alert') {
      return 'warning';
    }
    if (type === 'success' || type === 'complete') {
      return 'check_circle';
    }
    if (type === 'info') {
      return 'info';
    }
    return 'notifications';
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const token = getAuthToken();
      await fetch(`${API_BASE_URL}/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ read: true })
      });
      refetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on a dropdown item button - don't close if it is
      if (event.target.closest('.user-dropdown-item')) {
        return; // Let the button's onClick handle it
      }
      
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target) && !event.target.closest('.search-trigger')) {
        setShowSearch(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target) && !event.target.closest('.topbar-notifications')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const syncProfilePhoto = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        const photo = stored.profile_image || stored.profileImage || null;
        setProfileImage(photo ? (getImageUrl(photo) || photo) : null);
      } catch {
        setProfileImage(null);
      }
    };

    syncProfilePhoto();
    window.addEventListener('user-profile-updated', syncProfilePhoto);
    window.addEventListener('storage', syncProfilePhoto);
    return () => {
      window.removeEventListener('user-profile-updated', syncProfilePhoto);
      window.removeEventListener('storage', syncProfilePhoto);
    };
  }, []);

  // Keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setShowSearch(true);
        setTimeout(() => {
          const searchInput = document.querySelector('.topbar-search-input');
          if (searchInput) searchInput.focus();
        }, 100);
      }
      if (event.key === 'Escape') {
        setShowSearch(false);
        setShowUserMenu(false);
        setShowNotifications(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Update search results when data changes
  useEffect(() => {
    if (searchData) {
      const formatted = {
        users: searchData.users.map(r => formatSearchResult(r, 'user')),
        courses: searchData.courses.map(r => formatSearchResult(r, 'course')),
        content: searchData.content.map(r => formatSearchResult(r, 'content')),
        total: searchData.total
      };
      setSearchResults(formatted);
    }
  }, [searchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery);
    }
    setShowSearch(false);
  };

  const handleResultClick = (result) => {
    if (result.url && onTabChange) {
      // Extract tab ID from URL hash or use result type
      const tabId = result.url.split('#')[1] || result.type + '-management';
      onTabChange(tabId);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    // Clear all localStorage items
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('refreshToken');
    
    // Clear all sessionStorage items
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('adminUser');
    
    // Redirect to landing page
    window.location.href = '/';
  };

  const handleProfileSettings = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUserMenu(false);
    // Navigate to Profile Settings tab
    if (onTabChange) {
      onTabChange('profile-settings');
    }
  };

  const handleAccountSettings = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUserMenu(false);
    // Navigate to Account Settings tab
    if (onTabChange) {
      onTabChange('account-settings');
    }
  };

  const handleActivityHistory = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUserMenu(false);
    // Navigate to Overview tab which has Activity sub-tab showing activity history
    if (onTabChange) {
      onTabChange('overview');
    }
  };

  const handleLogoutClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleLogout();
  };

  const getInitials = (name) => {
    if (!name) {
      // Fallback: try to get initials from user object
      if (user.first_name && user.last_name) {
        return (user.first_name[0] + user.last_name[0]).toUpperCase();
      }
      if (user.first_name) {
        return user.first_name.substring(0, 2).toUpperCase();
      }
      return 'U';
    }
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="topbar">
      <div className="topbar-left">
        {/* Mobile Menu Toggle Button */}
        {onMenuToggle && (
          <button
            className="mobile-menu-toggle"
            onClick={onMenuToggle}
            aria-label="Toggle menu"
            aria-expanded={sidebarOpen || false}
          >
            <i className="material-icons">{sidebarOpen ? 'close' : 'menu'}</i>
          </button>
        )}
        
        {/* Global Search */}
        <div className="topbar-search-wrapper" ref={searchRef}>
          <button
            className="search-trigger"
            onClick={() => setShowSearch(!showSearch)}
            title="Search (Ctrl+K / Cmd+K)"
          >
            <i className="material-icons">search</i>
            <span className="search-placeholder">{t('buttons.search', { ns: 'common' })}...</span>
            <span className="search-shortcut">⌘K</span>
          </button>
          
          {showSearch && (
            <div className="topbar-search-dropdown">
              <form onSubmit={handleSearch} className="topbar-search-form">
                <i className="material-icons search-icon">search</i>
                <input
                  type="text"
                  className="topbar-search-input"
                  placeholder={t('search.placeholder', { ns: 'common' })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                <button type="submit" className="search-submit-btn">
                  <i className="material-icons">arrow_forward</i>
                </button>
              </form>
              {/* Search Results */}
              {searchQuery.trim().length >= 2 && (
                <div className="search-results">
                  {searching ? (
                    <div className="search-loading">
                      <i className="material-icons spinning">hourglass_empty</i>
                      <span>{t('search.searching', { ns: 'common' })}</span>
                    </div>
                  ) : searchResults && searchResults.total > 0 ? (
                    <>
                      {searchResults.users.length > 0 && (
                        <div className="search-results-section">
                          <div className="search-results-header">
                            <i className="material-icons">people</i>
                            <span>Users ({searchResults.users.length})</span>
                          </div>
                          {searchResults.users.map((result) => (
                            <div
                              key={result.id}
                              className="search-result-item"
                              onClick={() => handleResultClick(result)}
                            >
                              <i className="material-icons">{result.icon}</i>
                              <div className="search-result-content">
                                <div className="search-result-title">{result.title}</div>
                                <div className="search-result-subtitle">{result.subtitle}</div>
                                {result.metadata && (
                                  <div className="search-result-meta">
                                    {result.metadata.role && <span>{result.metadata.role}</span>}
                                    {result.metadata.status && <span>{result.metadata.status}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {searchResults.courses.length > 0 && (
                        <div className="search-results-section">
                          <div className="search-results-header">
                            <i className="material-icons">school</i>
                            <span>Courses ({searchResults.courses.length})</span>
                          </div>
                          {searchResults.courses.map((result) => (
                            <div
                              key={result.id}
                              className="search-result-item"
                              onClick={() => handleResultClick(result)}
                            >
                              <i className="material-icons">{result.icon}</i>
                              <div className="search-result-content">
                                <div className="search-result-title">{result.title}</div>
                                <div className="search-result-subtitle">{result.subtitle}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {searchResults.content.length > 0 && (
                        <div className="search-results-section">
                          <div className="search-results-header">
                            <i className="material-icons">article</i>
                            <span>Content ({searchResults.content.length})</span>
                          </div>
                          {searchResults.content.map((result) => (
                            <div
                              key={result.id}
                              className="search-result-item"
                              onClick={() => handleResultClick(result)}
                            >
                              <i className="material-icons">{result.icon}</i>
                              <div className="search-result-content">
                                <div className="search-result-title">{result.title}</div>
                                <div className="search-result-subtitle">{result.subtitle}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="search-results-footer">
                        <button
                          className="view-all-results-btn"
                          onClick={handleSearch}
                        >
                          {t('search.viewAllResults', { query: searchQuery, ns: 'common' })}
                        </button>
                      </div>
                    </>
                  ) : searchQuery.trim().length >= 2 ? (
                    <div className="search-no-results">
                      <i className="material-icons">search_off</i>
                      <span>{t('search.noResults', { query: searchQuery, ns: 'common' })}</span>
                    </div>
                  ) : (
                    <div className="search-suggestions">
                      <div className="search-suggestion-item">
                        <i className="material-icons">people</i>
                        <span>{t('search.searchUsers', { ns: 'common' })}</span>
                      </div>
                      <div className="search-suggestion-item">
                        <i className="material-icons">school</i>
                        <span>{t('search.searchCourses', { ns: 'common' })}</span>
                      </div>
                      <div className="search-suggestion-item">
                        <i className="material-icons">article</i>
                        <span>{t('search.searchContent', { ns: 'common' })}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="topbar-right">
        {/* Language Switcher */}
        <LanguageSwitcher className="topbar-language-switcher" />

        {/* Help & Documentation */}
        <button 
          className="topbar-icon-btn"
          onClick={() => window.open('/help-center', '_blank')}
          title={t('help', { ns: 'navigation' })}
        >
          <i className="material-icons">help_outline</i>
        </button>

        {/* Notifications */}
        <div className="topbar-notifications-wrapper" ref={notificationsRef}>
          <button 
            className="topbar-icon-btn topbar-notifications"
            onClick={() => setShowNotifications(!showNotifications)}
            title={t('notifications.label', { ns: 'navigation' })}
          >
            <NotificationBadge unreadCount={unreadCount} />
          </button>

          {showNotifications && (
            <div className="topbar-notifications-dropdown">
              <div className="notifications-dropdown-header">
                <h3>
                  {roleKey === 'student' ? t('notifications.myNotifications', { ns: 'common' }) : 
                   roleKey === 'teacher' ? t('notifications.teacherNotifications', { ns: 'common' }) : 
                   t('notifications.label', { ns: 'navigation' })}
                </h3>
                <button 
                  className="view-all-btn"
                  onClick={() => {
                    setShowNotifications(false);
                    if (onNotificationClick) onNotificationClick();
                  }}
                >
                  {t('notifications.viewAll', { ns: 'common' })}
                </button>
              </div>
              <div className="notifications-dropdown-list">
                {notificationsLoading ? (
                  <div className="notification-loading">
                    <i className="material-icons spinning">hourglass_empty</i>
                    <span>{t('messages.loading', { ns: 'common' })}</span>
                  </div>
                ) : notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div 
                      key={notification.id}
                      className={`notification-item ${!notification.read_status ? 'unread' : ''}`}
                      onClick={() => {
                        if (!notification.read_status) {
                          markAsRead(notification.id);
                        }
                      }}
                    >
                      <div className="notification-icon">
                        <i className="material-icons">{getNotificationIcon(notification.type, notification.category)}</i>
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        {notification.message && (
                          <div className="notification-message">{notification.message}</div>
                        )}
                        <div className="notification-time">{formatNotificationTime(notification.timestamp || notification.created_at)}</div>
                      </div>
                      {!notification.read_status && (
                        <div className="notification-unread-indicator"></div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="notification-empty">
                    <i className="material-icons">notifications_none</i>
                    <span>{t('notifications.none', { ns: 'common' })}</span>
                    <p>{t('notifications.allCaughtUp', { ns: 'common' })}</p>
                  </div>
                )}
              </div>
              {onNotificationClick && notifications.length > 0 && (
                <div className="notifications-dropdown-footer">
                  <button 
                    className="view-all-footer-btn"
                    onClick={() => {
                      setShowNotifications(false);
                      onNotificationClick();
                    }}
                  >
                    {t('notifications.viewAllNotifications', { ns: 'common' })}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="topbar-user-menu" ref={userMenuRef}>
          <button
            className="topbar-user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="topbar-user-avatar">
              {profileImage ? (
                <img src={profileImage} alt="" onError={() => setProfileImage(null)} />
              ) : (
                getInitials(userName)
              )}
            </div>
            <div className="topbar-user-info">
              <div className="topbar-user-name-row">
                <span className="topbar-user-name">{userName}</span>
                {typeof isPremium === 'boolean' && (
                  <span className={`account-badge ${isPremium ? 'premium' : 'free'}`}>
                    {isPremium ? (t('badges.premium', { ns: 'common' }) || 'Premium') : (t('badges.free', { ns: 'common' }) || 'Free')}
                  </span>
                )}
              </div>
              <span className="topbar-user-role">{userRoleLabel}</span>
            </div>
            <i className="material-icons">arrow_drop_down</i>
          </button>

          {showUserMenu && (
            <div className="topbar-user-dropdown">
              <div className="user-dropdown-header">
                <div className="user-dropdown-avatar">
                  {profileImage ? (
                    <img src={profileImage} alt="" onError={() => setProfileImage(null)} />
                  ) : (
                    getInitials(userName)
                  )}
                </div>
                <div className="user-dropdown-info">
                  <div className="user-dropdown-name">{userName}</div>
                  <div className="user-dropdown-email">{userEmail}</div>
                  {typeof isPremium === 'boolean' && (
                    <>
                      <div className="user-dropdown-plan">
                        {isPremium ? (t('badges.premium', { ns: 'common' }) || 'Premium') : (t('badges.free', { ns: 'common' }) || 'Free')}
                      </div>
                      {isPremium && subscriptionEndsAt && (
                        <div className="user-dropdown-expires">
                          {t('badges.expires', { ns: 'common' }) || 'Expires'} {new Date(subscriptionEndsAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              {typeof isPremium === 'boolean' && !isPremium && onUpgradeClick && (
                <>
                  <button type="button" className="user-dropdown-item user-dropdown-upgrade" onClick={(e) => { e.preventDefault(); setShowUserMenu(false); onUpgradeClick(); }}>
                    <i className="material-icons">workspace_premium</i>
                    <span>{t('badges.upgrade', { ns: 'common' }) || 'Upgrade'}</span>
                  </button>
                  <div className="user-dropdown-divider"></div>
                </>
              )}
              <button className="user-dropdown-item" onClick={handleProfileSettings}>
                <i className="material-icons">person</i>
                <span>{t('menu.profileSettings', { ns: 'common' })}</span>
              </button>
              <button className="user-dropdown-item" onClick={handleAccountSettings}>
                <i className="material-icons">settings</i>
                <span>{t('menu.accountSettings', { ns: 'common' })}</span>
              </button>
              <button className="user-dropdown-item" onClick={handleActivityHistory}>
                <i className="material-icons">history</i>
                <span>{t('menu.activityHistory', { ns: 'common' })}</span>
              </button>
              <div className="user-dropdown-divider"></div>
              <button className="user-dropdown-item" onClick={handleLogoutClick}>
                <i className="material-icons">logout</i>
                <span>{t('menu.logout', { ns: 'common' })}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopBar;

